import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faChartLine,
    faInfoCircle,
    faCheckCircle,
    faHourglassHalf,
    faTimesCircle,
    faTrophy,
    faExclamationTriangle,
    faBan
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import AdjustmentDetailModal from '../../components/AdjustmentDetailModal';

const InterviewerPayments = () => {
    const [payments, setPayments] = useState([]);
    const [statistics, setStatistics] = useState({
        total_payments: 0,
        total_paid: 0,
        pending_amount: 0,
        total_completes: 0,
        total_bonuses: 0,
        total_penalties: 0,
        total_carryover: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
    const [selectedAdjustment, setSelectedAdjustment] = useState(null);

    useEffect(() => {
        fetchPayments();
    }, []);

    const fetchPayments = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await api.get('/cati/interviewer/payments');

            if (response.data.success) {
                setPayments(response.data.data.payments);
                setStatistics(response.data.data.statistics);
            } else {
                setError(response.data.message || 'Erreur lors du chargement des paiements');
            }
        } catch (error) {
            console.error('Error fetching payments:', error);
            setError('Erreur lors du chargement de l\'historique des paiements');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (payment) => {
        if (payment.payment_status === 'excluded') {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    <FontAwesomeIcon icon={faBan} className="mr-1" />
                    Exclu
                </span>
            );
        }

        const statusMap = {
            'draft': { label: 'En Préparation', color: 'bg-yellow-100 text-yellow-700', icon: faHourglassHalf },
            'confirmed': { label: 'Traité', color: 'bg-green-100 text-green-700', icon: faCheckCircle },
            'validated': { label: 'Traité', color: 'bg-green-100 text-green-700', icon: faCheckCircle },
            'locked': { label: 'En Vérification', color: 'bg-blue-100 text-blue-700', icon: faInfoCircle },
            'exported': { label: 'Traité', color: 'bg-green-100 text-green-700', icon: faCheckCircle }
        };

        const statusInfo = statusMap[payment.status] || statusMap['draft'];

        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                <FontAwesomeIcon icon={statusInfo.icon} className="mr-1" />
                {statusInfo.label}
            </span>
        );
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleAdjustmentClick = (adjustment, payment) => {
        // Add additional context to the adjustment
        const adjustmentWithContext = {
            ...adjustment,
            batch_ref: payment.batch_ref,
            created_at: adjustment.at || payment.created_at  // Use adjustment timestamp if available
        };
        setSelectedAdjustment(adjustmentWithContext);
        setShowAdjustmentModal(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement de l'historique des paiements...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FontAwesomeIcon icon={faTimesCircle} className="text-5xl text-red-600 mb-4" />
                    <p className="text-red-600 text-lg">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                        Historique des Paiements
                    </h1>
                    <p className="text-gray-600">Consultez vos lots de paiement par enquête avec les détails des ajustements</p>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Total Paid */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-600">Total Reçu</h3>
                            <FontAwesomeIcon icon={faMoneyBillWave} className="text-2xl text-green-600" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {formatCurrency(statistics.total_paid)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            Montants effectivement reçus
                        </p>
                    </div>

                    {/* Total Completes */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-600">Total Complétés</h3>
                            <FontAwesomeIcon icon={faChartLine} className="text-2xl text-blue-600" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {statistics.total_completes || 0}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            Enquêtes complétées
                        </p>
                    </div>

                    {/* Total Bonuses */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-600">Total Bonus</h3>
                            <FontAwesomeIcon icon={faTrophy} className="text-2xl text-green-600" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {formatCurrency(statistics.total_bonuses || 0)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            Récompenses cumulées
                        </p>
                    </div>

                    {/* Total Penalties */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-gray-600">Total Pénalités</h3>
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl text-red-600" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900">
                            {formatCurrency(statistics.total_penalties || 0)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            Ajustements négatifs
                        </p>
                    </div>
                </div>

                {/* Total Carryover - Full Width Card */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-gray-600">Total des Montants Reportés</h3>
                        <FontAwesomeIcon icon={faMoneyBillWave} className="text-2xl text-purple-600" />
                    </div>
                    <div className="text-3xl font-bold text-purple-600">
                        +{formatCurrency(statistics.total_carryover || 0)}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                        Montants reportés des lots précédents
                    </p>
                </div>

                {/* Payments Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900">Détails des Paiements</h2>
                    </div>

                    {payments.length === 0 ? (
                        <div className="text-center py-12">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="text-5xl text-gray-300 mb-4" />
                            <p className="text-gray-500">Aucun lot de paiement disponible pour le moment</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Lot
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Enquête
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Quantité
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Prix Unit.
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Brut
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ajustements
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Net
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Statut
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {payments.map((payment) => {
                                        const isExcluded = payment.payment_status === 'excluded';
                                        return (
                                        <React.Fragment key={payment.id}>
                                        <tr className={`hover:bg-gray-50 transition-colors ${isExcluded ? 'opacity-60' : ''}`}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {payment.batch_ref}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-900">
                                                    {payment.survey_title}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`text-sm ${isExcluded ? 'line-through text-gray-400' : 'text-gray-900'}`}>{payment.qty}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`text-sm ${isExcluded ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                                    {formatCurrency(payment.unit_price)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`text-sm font-medium ${isExcluded ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                                    {formatCurrency(payment.total)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {payment.adjustments && payment.adjustments.length > 0 ? (
                                                    <div className="space-y-2">
                                                        {payment.adjustments.map((adj, idx) => (
                                                            <button
                                                                key={idx}
                                                                onClick={() => handleAdjustmentClick(adj, payment)}
                                                                className={`block text-sm font-medium hover:underline transition-colors duration-200 ${
                                                                    adj.amount > 0 ? 'text-green-600 hover:text-green-700' : 'text-red-600 hover:text-red-700'
                                                                }`}
                                                            >
                                                                {adj.amount > 0 ? '+' : ''}{formatCurrency(adj.amount)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`text-sm font-bold ${isExcluded ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                                    {formatCurrency(payment.net)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(payment)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-500">
                                                    {formatDate(payment.created_at)}
                                                </span>
                                            </td>
                                        </tr>
                                        {isExcluded && payment.exclusion_reason && (
                                            <tr>
                                                <td colSpan={9} className="px-6 py-2 bg-amber-50 border-b border-amber-100">
                                                    <div className="flex items-center gap-2 text-sm text-amber-700">
                                                        <FontAwesomeIcon icon={faBan} className="text-amber-500" />
                                                        <span>Raison : {payment.exclusion_reason}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Payment Method Info */}
                {payments.length > 0 && payments[0].payment_method && (
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
                        <div className="flex items-start">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 text-xl mt-1 mr-3" />
                            <div>
                                <h3 className="text-sm font-semibold text-blue-900 mb-2">Méthode de Paiement</h3>
                                <p className="text-sm text-blue-700">
                                    {payments[0].payment_method === 'bank' 
                                        ? `Versement bancaire - ${payments[0].bank_name || 'Banque'}`
                                        : `Service de transfert - ${payments[0].transfer_service || 'Service'}`
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Adjustment Detail Modal */}
            <AdjustmentDetailModal
                isOpen={showAdjustmentModal}
                onClose={() => {
                    setShowAdjustmentModal(false);
                    setSelectedAdjustment(null);
                }}
                adjustment={selectedAdjustment}
                showAdminInfo={false}
            />
        </div>
    );
};

export default InterviewerPayments;

